/**
 * <sound-toggle-button>
 *
 * Toggles the audio of a muted autoplay video. It lives inside a <native-video>, <external-video> or a
 * .video-wrapper and finds the underlying <video> / <iframe> at click time (the media is created lazily by
 * the theme's own video elements, so it may not exist yet when this connects).
 *
 * State: videos start muted, so the host carries `is-muted` on render. Clicking flips the state, swaps the
 * icon (handled purely with CSS via the `is-muted` class) and applies mute/unmute to the media.
 */
if (!customElements.get("sound-toggle-button")) {
  customElements.define(
    "sound-toggle-button",
    class SoundToggleButton extends HTMLElement {
      connectedCallback() {
        if (this._initialized) {
          return;
        }

        this.button = this.querySelector("[data-sound-toggle]");

        // If the defining script runs mid-parse, this element is upgraded before its child
        // <button> is parsed, so the button isn't here yet. Retry on the next frame until it is.
        if (!this.button) {
          requestAnimationFrame(() => this.connectedCallback());
          return;
        }

        this._initialized = true;
        this.provider = this.getAttribute("data-provider") || "native";
        this.muted = true;
        this._onClick = this._onClick.bind(this);
        this.button.addEventListener("click", this._onClick);
      }

      disconnectedCallback() {
        if (this.button) {
          this.button.removeEventListener("click", this._onClick);
        }
      }

      _onClick(event) {
        // Stop the click from reaching the video wrapper (which may treat clicks as play/pause).
        event.preventDefault();
        event.stopPropagation();

        this.muted = !this.muted;
        this._render();
        this._applyToMedia();
      }

      _render() {
        this.classList.toggle("is-muted", this.muted);
        this.button.setAttribute("aria-pressed", String(!this.muted));

        const label = this.muted
          ? this.getAttribute("data-label-unmute")
          : this.getAttribute("data-label-mute");

        if (label) {
          this.button.setAttribute("aria-label", label);
          this.button.setAttribute("title", label);
        }
      }

      _applyToMedia() {
        if (this.provider === "youtube" || this.provider === "vimeo") {
          this._applyToIframe();
        } else {
          this._applyToNative();
        }
      }

      _applyToNative() {
        const container = this.closest("native-video") || this.closest(".video-wrapper") || this.parentElement;
        const video = container && container.querySelector("video");

        if (!video) {
          return;
        }

        video.muted = this.muted;

        // A muted video can still have volume 0 — make sure unmuting is actually audible.
        if (!this.muted && video.volume === 0) {
          video.volume = 1;
        }
      }

      _applyToIframe() {
        const container = this.closest("external-video") || this.closest(".video-wrapper") || this.parentElement;
        const iframe = container && container.querySelector("iframe");

        if (!iframe || !iframe.contentWindow) {
          return;
        }

        const post = (message) => iframe.contentWindow.postMessage(JSON.stringify(message), "*");

        if (this.provider === "youtube") {
          post({ event: "command", func: this.muted ? "mute" : "unMute", args: "" });
          if (!this.muted) {
            post({ event: "command", func: "setVolume", args: [100] });
          }
        } else if (this.provider === "vimeo") {
          post({ method: "setMuted", value: this.muted });
          post({ method: "setVolume", value: this.muted ? 0 : 1 });
        }
      }
    }
  );
}
